import type { Permission } from "@/lib/auth";

export type MobileDestinationKey = "home" | "sales" | "stock" | "packing";

export type MobileDestination = {
  key: MobileDestinationKey;
  label: string;
  href: string;
  permission: Permission;
  matchPrefixes?: string[];
};

/**
 * The four fixed destinations in the mobile dock. Keep this list deliberately
 * small and canonical: tenant-customised sidebar items belong in More, not in
 * the primary thumb navigation.
 */
export const MOBILE_DESTINATIONS: readonly MobileDestination[] = [
  { key: "home", label: "Home", href: "/", permission: "view_dashboard" },
  { key: "sales", label: "Sales", href: "/sales", permission: "view_sales" },
  { key: "stock", label: "Stock", href: "/inventory", permission: "view_inventory" },
  { key: "packing", label: "Packing", href: "/packing", permission: "view_packing" },
];

export function isMobileDestinationActive(
  location: string,
  destination: MobileDestination,
): boolean {
  if (destination.key === "home") return location === "/";
  const prefixes = destination.matchPrefixes ?? [destination.href];
  return prefixes.some(
    (prefix) => location === prefix || location.startsWith(`${prefix}/`),
  );
}

export function isMobileMoreActive(location: string): boolean {
  return !MOBILE_DESTINATIONS.some((destination) =>
    isMobileDestinationActive(location, destination),
  );
}

export function canUseMobileDestination(
  has: (permission: Permission) => boolean,
  destination: MobileDestination,
): boolean {
  return has(destination.permission);
}

export function flattenMobileNavItems<T extends { href: string; children: T[] }>(
  items: T[],
): T[] {
  return items.flatMap((item) => [item, ...item.children]);
}